import { Component } from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Device, DeviceStatus, DeviceType } from "../../models/devices";
import { DevicesService } from "../../services/graphQl/devices/devices.service";
import { debounceTime, distinctUntilChanged, Subject, switchMap, takeUntil } from "rxjs";
import { DevicesFilterComponent } from "../devices-filter/devices-filter.component";
import { SortDirection } from "../../models/common";
import { DatePipe } from "@angular/common";

@Component({
  selector: "app-devices",
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, DevicesFilterComponent, DatePipe],
  templateUrl: "./devices.component.html",
  styleUrl: "./devices.component.css",
})
export class DevicesComponent {
  private destroy$ = new Subject<void>();

  showFilter = false;

  filterControl = new FormControl("");
  pageSizeControl = new FormControl(10);

  devices: Device[] = [];
  paginatedDevice: Device[][] = [];

  currentPage: number = 0;
  currentPageDevices: Device[] = [];

  sortField: keyof Device | undefined = undefined;
  sortDirection: SortDirection | undefined = undefined;

  deviceStatusEnum = DeviceStatus;
  deviceTypeEnum = DeviceType;
  sortDirectionEnum = SortDirection;

  constructor(private devicesService: DevicesService) {}

  ngOnInit(): void {
    this.devicesService
      .listAllDevices()
      .pipe(takeUntil(this.destroy$))
      .subscribe((devices: Device[]) => {
        this.devices = [...devices];
        this.paginateAndSortDevices();
      });

    this.filterControl.valueChanges
      .pipe(
        debounceTime(500), // espera 500ms
        distinctUntilChanged() // evita repeticiones
      )
      .subscribe((inputValue) => {
        this.devicesService.setFilterQuery(inputValue || "");
      });

    this.pageSizeControl.valueChanges.subscribe((inputValue) => {
      this.paginateAndSortDevices();
    });
  }

  ngOnDestroy(): void {
    this.devicesService.clearFilter();
    this.destroy$.next();
    this.destroy$.complete();
  }

  openFilters() {
    console.log("openFilters");
    this.showFilter = true;
  }

  closeFilters() {
    this.showFilter = false;
  }

  clearFilter() {
    this.filterControl.setValue("", { emitEvent: false });
  }

  paginateAndSortDevices(): void {
    this.paginatedDevice = [];
    const devices = this.sortDevices();
    const pageSize = Number(this.pageSizeControl.value);
    for (let i = 0; i < devices.length; i += pageSize) {
      this.paginatedDevice.push(devices.slice(i, i + pageSize));
    }
    this.currentPage = 0;
    this.setCurrentPage();
  }

  setCurrentPage(): void {
    this.currentPageDevices = this.paginatedDevice[this.currentPage];
  }

  changePage(page: number) {
    this.currentPage = page;
    this.setCurrentPage();
  }

  sort(field: keyof Device) {
    this.sortField = field;
    switch (this.sortDirection) {
      case SortDirection.ASC:
        this.sortDirection = SortDirection.DESC;
        break;
      case SortDirection.DESC:
        this.sortDirection = undefined;
        break;
      default:
        this.sortDirection = SortDirection.ASC;
        break;
    }
    this.paginateAndSortDevices();
  }

  isSortedBy(field: keyof Device, direction: SortDirection): boolean {
    return this.sortField === field && this.sortDirection === direction;
  }

  sortDevices(): Device[] {
    let sorted = [...this.devices];
    if (this.sortField && this.sortDirection) {
      console.log("this.sortField", this.sortField);
      console.log("this.sortDirection", this.sortDirection);

      sorted = sorted.sort((a: Device, b: Device) => {
        let valueA = a[this.sortField];
        let valueB = b[this.sortField];

        // Normalizar: null/undefined → ""
        if (valueA == null) valueA = "";
        if (valueB == null) valueB = "";

        // Trim para que espacios en blanco se consideren vacío
        if (typeof valueA === "string") valueA = valueA.trim();
        if (typeof valueB === "string") valueB = valueB.trim();

        const isEmptyA = valueA === "";
        const isEmptyB = valueB === "";

        // Reglas especiales para vacíos/nulos
        if (isEmptyA && !isEmptyB) {
          return this.sortDirection === SortDirection.ASC ? -1 : 1;
        }
        if (!isEmptyA && isEmptyB) {
          return this.sortDirection === SortDirection.ASC ? 1 : -1;
        }

        // if (valueA == null || valueB == null) return 0;

        // Si son strings, usar localeCompare para ignorar mayúsculas/minúsculas
        if (typeof valueA === "string" && typeof valueB === "string") {
          const result = valueA.localeCompare(valueB, undefined, { sensitivity: "base" });
          return this.sortDirection === SortDirection.ASC ? result : -result;
        }

        // Comparación genérica (string, number, date en string ISO, etc.)
        if (valueA < valueB) return this.sortDirection === SortDirection.ASC ? -1 : 1;
        if (valueA > valueB) return this.sortDirection === SortDirection.ASC ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }
}
